import { Error } from "../internal";
import { LAYERPROPERTYTYPE, LAYERTYPE, Layer } from "./internal";
/**
 * @class WFSLayer
 */
export class WFSGeoserverLayer extends Layer {
    constructor(scene, opt_options) {
        super(scene, opt_options);
        this.type = LAYERTYPE.WFSLAYER;
        const options = opt_options || {};
        this.featureType = options.featureType || "";
        this.geometryName = options.geometryName || "the_geom";
        this.api = options.api;
    }
    /**
     * Loads all features within the extent
     * @param extent
     * @param srid
     */
    _loadFeatures_internal(extent, srid) {
        this.api.getFeature({
            service: "wfs",
            version: this.api.version,
            request: "GetFeature",
            typeName: this.featureType,
            geometryName: this.geometryName,
            srsName: "EPSG:" + srid,
            bbox: extent.minx + "," + extent.miny + "," + extent.maxx + "," + extent.maxy + "," + "EPSG:" + srid,
            outputFormat: "application/json",
        }, (fc) => {
            this.addFeatures(fc);
        }, (message) => {
            console.log(message);
        });
    }
    /**
     * Creates xml to send to wfs server
     * @param inserts
     * @param updates
     * @param deletions
     * @param cbs
     * @param cbf
     * @private
     */
    _saveFeatures_internal(inserts, updates, deletions, cbs, cbf) {
        const crs = inserts.crs.properties.name;
        this.api.applyEdits({
            featureType: this.featureType,
            geometryName: this.geometryName,
            srsName: crs,
        }, inserts, updates, deletions, (response) => {
            for (let i = 0; i < inserts.features.length; i++) {
                inserts.features[i].id = response.insertIds[i];
            }
            cbs(inserts, updates, deletions);
        }, (response) => {
            cbf(response);
        });
    }
    /**
     * Gets all features on this layer
     * @param cbs
     * @param cbf
     * @param options
     * @private
     */
    _exportFeatures_internal(cbs, cbf, options) {
        const countonly = typeof options.countonly === "undefined" ? false : options.countonly;
        this.api.readFeatures({
            service: "wfs",
            version: this.api.version,
            request: "GetFeature",
            typeName: this.featureType,
            geometryName: this.geometryName,
            startIndex: options.start,
            maxFeatures: options.limit,
            count: options.limit,
            srsName: "EPSG:" + options.srid,
            outputFormat: "application/json",
        }, this.filters, options.sorters || [], (fc) => {
            if (countonly) {
                cbs(fc.totalFeatures);
            }
            else {
                cbs(fc);
            }
        }, cbf);
    }
    /**
     * Returns converted options of layer described by dft
     * @param dft
     * @param opt_options
     * @returns {any|{}}
     */
    static optionsFromDescribeFeatureType(dft, opt_options) {
        const options = opt_options || {};
        options.layername = dft.typeName;
        options.featureType = dft.typeName;
        options["geometrytype"] = "undefined";
        options["propertydef"] = [];
        for (let i = 0; i < dft.properties.length; i++) {
            const prop = {};
            prop["key"] = dft.properties[i].name;
            prop["name"] = dft.properties[i].name;
            prop["datatype"] = LAYERPROPERTYTYPE.STRING;
            prop["defaultvalue"] = "test";
            let isgeom = false;
            switch (dft.properties[i].type) {
                case "xsd:number":
                    prop["datatype"] = LAYERPROPERTYTYPE.NUMBER;
                    prop["defaultvalue"] = 0;
                    break;
                case "xsd:string":
                    prop["datatype"] = LAYERPROPERTYTYPE.STRING;
                    prop["defaultvalue"] = "";
                    break;
                case "xsd:int":
                    prop["datatype"] = LAYERPROPERTYTYPE.INTEGER;
                    prop["defaultvalue"] = 0;
                    break;
                case "xsd:date":
                    prop["datatype"] = LAYERPROPERTYTYPE.DATE;
                    prop["defaultvalue"] = "1970-01-01Z";
                    prop["dateformat"] = "Y-m-d\\Z";
                    break;
                case "xsd:date-time":
                    prop["datatype"] = LAYERPROPERTYTYPE.DATE;
                    prop["defaultvalue"] = "1970-01-01T00:00:00Z";
                    prop["dateformat"] = "C";
                    break;
                case "gml:Point":
                    options["geometrytype"] = "Point";
                    options["geometryName"] = prop["name"];
                    isgeom = true;
                    break;
                case "gml:MultiPoint":
                    options["geometrytype"] = "MultiPoint";
                    options["geometryName"] = prop["name"];
                    isgeom = true;
                    break;
                case "gml:LineString":
                    options["geometrytype"] = "LineString";
                    options["geometryName"] = prop["name"];
                    isgeom = true;
                    break;
                case "gml:Polygon":
                    options["geometrytype"] = "Polygon";
                    options["geometryName"] = prop["name"];
                    isgeom = true;
                    break;
                case "gml:Box":
                case "gml:LinearRing":
                case "gml:MultiGeometry":
                case "gml:MultiLineString":
                    options["geometrytype"] = "MultiLineString";
                    options["geometryName"] = prop["name"];
                    isgeom = true;
                    break;
                case "gml:MultiPolygon":
                    options["geometrytype"] = "MultiPolygon";
                    options["geometryName"] = prop["name"];
                    isgeom = true;
                    break;
            }
            if (!isgeom)
                options["propertydef"].push(prop);
        }
        return options;
    }
    /**
     * creates FeatureStyle from sld
     * @param style
     * @returns {FeatureStyle}
     */
    static parseStyle(style) {
        const rule = style.getElementsByTagNameNS("http://www.opengis.net/sld", "Rule")[0];
        const fillNode = rule.querySelector(["[name='fill']"]);
        const strokeNode = rule.querySelector(["[name='stroke']"]);
        const sizeNode = rule.getElementsByTagNameNS("http://www.opengis.net/sld", "Size")[0];
        let symbolizer = "";
        //rule.children does not work in IE
        for (let j = 0; j < rule.childNodes.length; j++) {
            const childNode = rule.childNodes[j];
            if (childNode.nodeType === 1) {
                //only element nodes
                if (childNode.localName.indexOf("Symbolizer") > 0) {
                    symbolizer = childNode.localName.replace("Symbolizer", "").toLowerCase();
                }
            }
        }
        const styleOptions = {
            stylename: "layerstyle",
            rules: [],
        };
        const highlightOptions = {
            stylename: "highlightstyle",
            rules: [],
        };
        const params = {};
        const highlight = {};
        let strokecolor = [0, 0, 0, 1];
        if (strokeNode) {
            strokecolor = WFSGeoserverLayer.hexToRgbaArray(strokeNode.textContent);
        }
        else if (fillNode) {
            strokecolor = WFSGeoserverLayer.hexToRgbaArray(fillNode.textContent);
        }
        let fillcolor = [0, 0, 0, 0.7];
        if (fillNode)
            fillcolor = WFSGeoserverLayer.hexToRgbaArray(fillNode.textContent);
        switch (symbolizer) {
            case "point":
                params["size"] = sizeNode ? sizeNode.textContent : 5;
                params["stroke"] = strokecolor;
                highlight["size"] = params["size"];
                highlight["stroke"] = params["stroke"].map(function (value, idx) {
                    if (idx != 3)
                        return 1 - value;
                    return value;
                });
                break;
            case "line":
                params["stroke-width"] = sizeNode ? sizeNode.textContent : 5;
                params["stroke"] = strokecolor;
                highlight["stroke-width"] = params["stroke-width"];
                highlight["stroke"] = params["stroke"].map(function (value, idx) {
                    if (idx != 3) {
                        return 1 - value;
                    }
                    else {
                        return value;
                    }
                });
                styleOptions.rules.push({
                    filter: "",
                    symbolizer: "point",
                    params: {
                        size: params["stroke-width"],
                        stroke: params["stroke"],
                    },
                });
                highlightOptions.rules.push({
                    filter: "",
                    symbolizer: "point",
                    params: {
                        size: highlight["stroke-width"],
                        stroke: highlight["stroke"],
                    },
                });
                break;
            case "polygon":
                params["stroke-width"] = sizeNode ? sizeNode.textContent : 1;
                params["fill"] = fillcolor;
                params["stroke"] = strokecolor;
                highlight["stroke-width"] = params["stroke-width"];
                highlight["fill"] = params["fill"].map(function (value, idx) {
                    if (idx != 3) {
                        return 1 - value;
                    }
                    else {
                        return value;
                    }
                });
                highlight["stroke"] = params["stroke"].map(function (value, idx) {
                    if (idx != 3) {
                        return 1 - value;
                    }
                    else {
                        return value;
                    }
                });
                styleOptions.rules.push({
                    filter: "",
                    symbolizer: "point",
                    params: {
                        size: params["stroke-width"],
                        stroke: params["stroke"],
                    },
                });
                styleOptions.rules.push({
                    filter: "",
                    symbolizer: "line",
                    params: {
                        "stroke-width": params["stroke-width"],
                        stroke: params["stroke"],
                    },
                });
                highlightOptions.rules.push({
                    filter: "",
                    symbolizer: "point",
                    params: {
                        size: highlight["stroke-width"],
                        stroke: highlight["stroke"],
                    },
                });
                highlightOptions.rules.push({
                    filter: "",
                    symbolizer: "line",
                    params: {
                        "stroke-width": highlight["stroke-width"],
                        stroke: highlight["stroke"],
                    },
                });
                break;
        }
        styleOptions.rules.push({
            filter: "",
            symbolizer: symbolizer,
            params: params,
        });
        highlightOptions.rules.push({
            filter: "",
            symbolizer: symbolizer,
            params: highlight,
        });
        return {
            layerstyle: styleOptions,
            highlightstyle: highlightOptions,
        };
    }
    /**
     * translates hex to rgba array (with alpha 0.7)
     * @param hex
     * @returns {number[]}
     */
    static hexToRgbaArray(hex) {
        let c;
        if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
            c = hex.substring(1).split("");
            if (c.length == 3) {
                c = [c[0], c[0], c[1], c[1], c[2], c[2]];
            }
            c = "0x" + c.join("");
            return [((c >> 16) & 255) / 255, ((c >> 8) & 255) / 255, (c & 255) / 255, 0.7];
        }
        throw new Error("Bad Hex");
    }
}
//# sourceMappingURL=wfsgeoserverlayer.js.map